import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Box, Upload, Download, Loader2, ImageIcon, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PRESETS = [
  { id: "optimized", label: "Game Ready — Optimized", desc: "1024px mesh // 2048px texture // ~50k vertices. Fast, engine-light props.", params: { resolution: 1024, texture_size: 2048, decimation_target: 50000 } },
  { id: "high", label: "High Quality — Game Ready", desc: "1536px mesh // 2048px texture // ~100k vertices. The sweet spot for hero assets.", params: { resolution: 1536, texture_size: 2048, decimation_target: 100000 } },
  { id: "maximum", label: "Maximum Fidelity", desc: "1536px mesh // 4096px texture // ~500k vertices. Cinematic quality, heavy file.", params: { resolution: 1536, texture_size: 4096, decimation_target: 500000 } },
];

function loadModelViewer() {
  if (document.querySelector('script[data-model-viewer]')) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.type = "module";
    s.src = "https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js";
    s.dataset.modelViewer = "true";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function GlbViewer({ jobId, glbSize, base }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    let mounted = true;
    loadModelViewer()
      .then(() => api.get(`${base}/jobs/${jobId}/glb`, { responseType: "blob" }))
      .then((r) => {
        if (!mounted) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => toast.error("Failed to load 3D preview"));
    return () => { mounted = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [jobId]);

  if (!src) {
    return (
      <div className="aspect-square border border-white/10 bg-black/60 grid-texture flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-6 h-6 text-nc-blue animate-spin" />
        <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em]">Loading model…</p>
      </div>
    );
  }
  return (
    <div data-testid="glb-viewer" className="aspect-square border border-nc-blue/30 bg-[#0a0d14]">
      <model-viewer
        src={src}
        camera-controls=""
        auto-rotate=""
        shadow-intensity="1"
        exposure="1"
        style={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
      />
      <p className="font-mono text-[8px] text-zinc-600 px-2 py-1 border-t border-white/10 uppercase tracking-[0.2em]">GLB // {(glbSize / 1024 / 1024).toFixed(1)} MB // drag to orbit</p>
    </div>
  );
}

export const HUB_PRESETS = [
  { id: "game_ready", label: "Game Ready — Optimized", desc: "1024px mesh // 2048px texture // ~50k vertices. Fast, engine-light props.", params: { preset: "game_ready" } },
  { id: "high_quality", label: "High Quality — Game Ready", desc: "1536px mesh // 2048px texture // ~100k vertices. The sweet spot for hero assets.", params: { preset: "high_quality" } },
];

export default function AdminAi3d({ base = "/admin/ai3d", presets = PRESETS, heading, note }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [preset, setPreset] = useState("high");
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.get(`${base}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const pick = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`${base}/jobs/${jobId}`);
        setActive(r.data);
        if (r.data.status === "done" || r.data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          if (r.data.status === "done") toast.success("3D model generated — game ready GLB");
          else toast.error(r.data.error || "Generation failed");
          load();
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const generate = async () => {
    if (!file) { toast.error("Pick an image first"); return; }
    setBusy(true);
    try {
      const p = presets.find((x) => x.id === preset).params;
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`${base}/generate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: p,
      });
      toast.info("Generation queued — TRELLIS.2 takes 1–4 minutes at high quality");
      setActive({ id: r.data.job_id, status: "queued", filename: file.name, params: p });
      pollJob(r.data.job_id);
    } catch (err) {
      setBusy(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const download = async (job) => {
    try {
      const r = await api.get(`${base}/jobs/${job.id}/glb`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.filename.rsplit(".", 1)[0]}-trellis.glb`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const running = active && (active.status === "queued" || active.status === "processing");

  return (
    <section data-testid="admin-ai3d" className="flex flex-col gap-6">
      <div className="border border-nc-blue/30 bg-nc-blue/5 p-5">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Box className="w-4 h-4" /> {heading || "AI 3D Lab // Microsoft TRELLIS.2 — Admin Only"}</p>
        <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
          {note || "Image → fully textured, remeshed, optimized game-ready GLB via fal.ai inference. Each generation bills to the Universal Key balance. Tip: clean product shots and concept art on plain backgrounds produce the best meshes."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input column */}
        <div className="flex flex-col gap-4">
          <div
            data-testid="ai3d-dropzone"
            onClick={() => fileRef.current?.click()}
            className="border border-dashed border-white/20 hover:border-nc-blue aspect-[4/3] cursor-pointer transition-colors flex items-center justify-center overflow-hidden bg-black/40"
          >
            {preview ? (
              <img src={preview} alt="Source" className="w-full h-full object-contain" />
            ) : (
              <div className="text-center p-6">
                <ImageIcon className="w-8 h-8 text-zinc-600 mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-mono text-xs text-zinc-400">Click to pick a source image</p>
                <p className="font-mono text-[9px] text-zinc-600 mt-2">PNG / JPG / WebP — max 8MB</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="ai3d-file-input" />

          <div className="flex flex-col gap-2">
            {presets.map((p) => (
              <button
                key={p.id}
                data-testid={`ai3d-preset-${p.id}`}
                onClick={() => setPreset(p.id)}
                className={`text-left border px-4 py-3 transition-colors ${preset === p.id ? "border-nc-blue bg-nc-blue/10" : "border-white/10 hover:border-white/30"}`}
              >
                <p className={`font-sans font-bold uppercase text-xs ${preset === p.id ? "text-nc-blue" : "text-zinc-100"}`}>{p.label}</p>
                <p className="font-mono text-[9px] text-zinc-500 mt-1">{p.desc}</p>
              </button>
            ))}
          </div>

          <button
            data-testid="ai3d-generate"
            onClick={generate}
            disabled={busy || !file}
            className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 flex items-center justify-center gap-2 transition-colors"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Box className="w-4 h-4" />}
            {running ? (active.status === "queued" ? "Queued…" : "Generating — this takes minutes…") : "Generate 3D Model"}
          </button>
          {active?.status === "failed" && (
            <p data-testid="ai3d-error" className="font-mono text-[10px] text-red-400 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {active.error}</p>
          )}
        </div>

        {/* Output column */}
        <div className="flex flex-col gap-4">
          {active?.status === "done" ? (
            <>
              <GlbViewer jobId={active.id} glbSize={active.glb_size} base={base} />
              <div className="flex items-center gap-3">
                <button data-testid="ai3d-download" onClick={() => download(active)} className="flex-1 bg-nc-green hover:bg-nc-green/80 text-white font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center justify-center gap-2 transition-colors">
                  <Download className="w-4 h-4" /> Download GLB
                </button>
              </div>
              <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">Drag straight into Unreal Engine 5, Unity or Blender. Mesh is remeshed with baked PBR textures at your chosen budget.</p>
            </>
          ) : (
            <div className="aspect-square border border-dashed border-white/15 bg-black/40 grid-texture flex flex-col items-center justify-center gap-3 p-8 text-center">
              {running ? (
                <>
                  <Loader2 className="w-8 h-8 text-nc-blue animate-spin" />
                  <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.2em]">{active.status === "queued" ? "Waiting for GPU slot…" : "TRELLIS.2 is building your model…"}</p>
                  <p className="font-mono text-[9px] text-zinc-600 max-w-xs">High quality generation takes 1–4 minutes. The page polls automatically — you can leave and come back.</p>
                </>
              ) : (
                <>
                  <Box className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.2em]">Your game-ready model appears here</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {jobs.length > 0 && (
        <div className="border border-white/10">
          <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Generation History ({jobs.length})</div>
          {jobs.map((j, i) => (
            <div key={j.id} data-testid={`ai3d-job-${i + 1}`} onClick={() => j.status === "done" && setActive(j)} className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 font-mono text-[10px] flex-wrap ${j.status === "done" ? "cursor-pointer hover:bg-white/5 transition-colors" : ""}`}>
              <span className="text-zinc-300 truncate max-w-[220px]">{j.filename}</span>
              <span className="text-zinc-600 shrink-0">res {j.params?.resolution} // tex {j.params?.texture_size} // {(j.params?.decimation_target / 1000).toFixed(0)}k verts</span>
              <span className={`shrink-0 uppercase tracking-[0.15em] text-[8px] border px-2 py-0.5 ${j.status === "done" ? "text-nc-green border-nc-green/40" : j.status === "failed" ? "text-red-400 border-red-400/40" : "text-nc-blue border-nc-blue/40 animate-pulse"}`}>{j.status}</span>
              <span className="text-zinc-600 shrink-0">{String(j.created_at).slice(5, 16).replace("T", " ")}</span>
              {j.status === "done" && (
                <button data-testid={`ai3d-job-download-${i + 1}`} onClick={() => download(j)} className="text-nc-green hover:text-white shrink-0 transition-colors"><Download className="w-3.5 h-3.5" /></button>
              )}
              {(j.status === "queued" || j.status === "processing") && (
                <button onClick={() => { setActive(j); setBusy(true); pollJob(j.id); }} className="text-nc-blue hover:text-white shrink-0 font-mono text-[8px] uppercase tracking-[0.15em]">Watch</button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
